import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, PencilLine, PlayCircle } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  LigneAlerte,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampRecherche } from '../components/ui/Onglets';
import { ParcoursCommercial } from '../components/commercial/ParcoursCommercial';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useEnregistrerIssue,
  useEntretiens,
  usePlanifierEntretien,
  useVerserCompteRendu,
  type EtatEntretien,
  type FicheEntretien,
  type LigneEntretien,
  type OrientationEntretien,
  type ReferentielFiche,
  type TriEntretiens,
  type TypeEntretien,
} from '../lib/useEntretiens';
import { ModaleFicheEntretien } from '../components/commercial/ModaleFicheEntretien';
import { ModaleCompteRendu, type Decision } from '../components/commercial/ModaleCompteRendu';
import { VisionneuseCompteRendu } from '../components/commercial/VisionneuseCompteRendu';
import { lignesCompletes, useApprenants, useModifierDossier, type ModeDossier } from '../lib/useApprenants';
import { useReferentiels } from '../lib/useDocuments';
import { useAuth } from '../auth/useAuth';
import { useEntreprises } from '../lib/useEntreprises';
import { useRelances } from '../lib/useRelances';
import { ErreurApi } from '../lib/api';
import { useToast } from '../components/ui/useToast';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const ETATS: { valeur: EtatEntretien; libelle: string }[] = [
  { valeur: 'PLANIFIE', libelle: 'Planifié' },
  { valeur: 'A_RELANCER', libelle: 'À relancer' },
  { valeur: 'REALISE', libelle: 'Réalisé' },
  { valeur: 'RETENU', libelle: 'Retenu' },
  { valeur: 'NON_RETENU', libelle: 'Non retenu' },
  { valeur: 'A_REVOIR', libelle: 'À revoir' },
];

/**
 * Écran « Suivi des entretiens » du pôle commercial (CDC §5.2).
 *
 * Un seul tableau pour les candidats et les entreprises : entretien de
 * recrutement, recueil des besoins, négociation, repositionnement après
 * rupture. Les entretiens saisis depuis l'écran des inscriptions y figurent
 * sans ressaisie — c'est la même table.
 */
export function ComEntretiensPage(): JSX.Element {
  const navigate = useNavigate();
  const notifier = useToast();
  const { site } = usePerimetre();
  const [type, setType] = useState<TypeEntretien | ''>('');
  const [etat, setEtat] = useState<EtatEntretien | ''>('');
  // Dernier ajouté ou modifié en tête par défaut : c'est ce sur quoi on vient
  // de travailler, pas nécessairement l'entretien le plus proche dans le temps.
  const [tri, setTri] = useState<TriEntretiens>('RECENT');
  const [recherche, setRecherche] = useState('');
  const [planification, setPlanification] = useState(false);
  // Identifiant seul, et non la ligne : après un enregistrement, la fenêtre
  // doit montrer la ligne rechargée, pas la copie figée à son ouverture.
  const [ficheOuverte, setFicheOuverte] = useState<string | null>(null);
  const [compteRenduOuvert, setCompteRenduOuvert] = useState<string | null>(null);
  const [pdfOuvert, setPdfOuvert] = useState<string | null>(null);

  const filtres = {
    site,
    ...(type ? { type } : {}),
    ...(etat ? { etat } : {}),
    ...(recherche.trim() ? { recherche: recherche.trim() } : {}),
    tri,
  };

  const { data, isLoading, error } = useEntretiens(filtres);
  const { data: relances } = useRelances(site);
  const planifier = usePlanifierEntretien();
  const issue = useEnregistrerIssue();
  const versement = useVerserCompteRendu();
  const dossierModifie = useModifierDossier();
  const { data: referentiels } = useReferentiels();
  const { peutAcceder } = useAuth();

  const compteurs = data?.compteurs;
  const entretiens = useMemo(() => data?.entretiens ?? [], [data?.entretiens]);

  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [site, type, etat, tri, recherche, taillePage]);

  const total = entretiens.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const entretiensAffiches = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return entretiens.slice(debut, debut + taillePage);
  }, [entretiens, page, taillePage]);

  const referentielFiche: ReferentielFiche | undefined = data?.referentielFiche;
  const entretienFiche = entretiens.find((ligne) => ligne.id === ficheOuverte) ?? null;
  const entretienCompteRendu = entretiens.find((ligne) => ligne.id === compteRenduOuvert) ?? null;
  const entretienPdf = entretiens.find((ligne) => ligne.id === pdfOuvert) ?? null;
  const erreurIssue =
    issue.error instanceof ErreurApi
      ? issue.error.message
      : versement.error instanceof ErreurApi
        ? versement.error.message
        : dossierModifie.error instanceof ErreurApi
          ? dossierModifie.error.message
          : null;

  /**
   * Verse le compte-rendu au dossier apprenant. La fenêtre reste ouverte : le
   * versement ne conclut pas l'entretien, la décision reste à prendre.
   */
  const verser = (id: string): void => {
    versement.mutate(id, {
      onSuccess: () => notifier('Compte-rendu et fiche de suivi versés au dossier apprenant'),
    });
  };

  /**
   * Enregistre la fiche. « Terminer » marque l'entretien réalisé et enchaîne
   * sur le compte-rendu : c'est là que se prend la décision, et l'enchaînement
   * évite d'avoir à retrouver la ligne dans le tableau.
   */
  const enregistrerFiche = (
    id: string,
    apprenantId: string | null,
    valeurs: {
      fiche: FicheEntretien;
      compteRendu: string;
      besoinsRecueillis?: string;
      dossier?: { mode: ModeDossier; formationCode: string | null };
    },
    terminer: boolean,
  ): void => {
    const { dossier, ...fiche } = valeurs;

    issue.mutate(
      { id, ...fiche, ...(terminer ? { resultat: 'REALISE' as const } : {}) },
      {
        onSuccess: () => {
          // Le rattachement du dossier est une seconde écriture, sur une autre
          // route et un autre droit : elle suit l'enregistrement de la fiche
          // plutôt que de le conditionner — une fiche d'entretien ne doit pas
          // se perdre parce que le dossier a refusé la bascule.
          if (dossier && apprenantId) {
            dossierModifie.mutate(
              { id: apprenantId, valeurs: dossier },
              { onSuccess: () => notifier('Rattachement du dossier mis à jour') },
            );
          }
          setFicheOuverte(null);
          notifier(terminer ? 'Entretien terminé · fiche enregistrée' : 'Fiche d’entretien enregistrée');
          if (terminer) setCompteRenduOuvert(id);
        },
      },
    );
  };

  /**
   * Enregistre la décision. La fenêtre se ferme et un message le confirme :
   * sans cela, l'issue n'était visible que sur la pastille en haut du
   * compte-rendu, hors de l'écran au moment du clic — on croyait le bouton
   * mort alors que le dossier avait bougé.
   */
  const trancher = (id: string, decision: Decision, orientation?: OrientationEntretien): void => {
    issue.mutate(
      { id, resultat: decision, ...(orientation ? { orientation } : {}) },
      {
        onSuccess: (ligne) => {
          setCompteRenduOuvert(null);
          notifier(`Entretien marqué « ${ligne.etatLibelle} »`);
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Suivi des entretiens"
        sousTitre="Chargé(e) de recrutement · entretiens candidats et comptes rendus"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <BoutonSecondaire onClick={() => navigate('/calendar')}>
              Vue calendrier
            </BoutonSecondaire>
            <BoutonPrincipal onClick={() => setPlanification((ouvert) => !ouvert)}>
              {planification ? 'Fermer' : '+ Planifier un entretien'}
            </BoutonPrincipal>
          </div>
        }
      />

      <ParcoursCommercial etape="entretien" />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Entretiens cette semaine"
          valeur={compteurs?.aVenir ?? '—'}
          detail={
            compteurs
              ? `${compteurs.candidats} candidat(s) · ${compteurs.entreprises} entreprise(s)`
              : 'périmètre affiché'
          }
          ton="navy"
        />
        <Compteur
          libelle="Candidats retenus"
          valeur={compteurs?.retenus ?? '—'}
          detail="issue favorable"
          ton="mint"
        />
        <Compteur
          libelle="À relancer"
          valeur={compteurs?.aRelancer ?? '—'}
          detail="date passée, issue non saisie"
          ton="warm"
        />
        <Compteur
          libelle="Réalisés"
          valeur={compteurs?.realises ?? '—'}
          detail="compte-rendu enregistré"
          ton="blue"
        />
      </div>

      {planification && (
        <FormulairePlanification
          site={site}
          charges={data?.chargesDisponibles ?? []}
          enCours={planifier.isPending}
          erreur={planifier.error instanceof ErreurApi ? planifier.error.message : null}
          onPlanifier={(entree) =>
            planifier.mutate(entree, { onSuccess: () => setPlanification(false) })
          }
        />
      )}

      <Carte
        titre="Tableau de suivi"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher un entretien"
              placeholder="Interlocuteur ou objet"
            />
            <select
              aria-label="Interlocuteurs"
              value={type}
              onChange={(evenement) => setType(evenement.target.value as TypeEntretien | '')}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs"
            >
              <option value="">Candidats et entreprises</option>
              <option value="CANDIDAT">Candidats</option>
              <option value="ENTREPRISE">Entreprises</option>
            </select>
            <select
              aria-label="Issue"
              value={etat}
              onChange={(evenement) => setEtat(evenement.target.value as EtatEntretien | '')}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs"
            >
              <option value="">Toutes les issues</option>
              {ETATS.map((entree) => (
                <option key={entree.valeur} value={entree.valeur}>
                  {entree.libelle}
                </option>
              ))}
            </select>
            <select
              aria-label="Tri"
              value={tri}
              onChange={(evenement) => setTri(evenement.target.value as TriEntretiens)}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs"
            >
              <option value="RECENT">Dernier ajouté ou modifié</option>
              <option value="ANCIEN">Plus ancien d’abord</option>
            </select>
          </div>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'Le tableau des entretiens n’a pas pu être chargé.'
              }
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement des entretiens…" />}

        {!isLoading && !error && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Interlocuteur', 'Type', 'Objet', 'Date', 'Chargé(e)', 'Issue', ''].map(
                    (colonne, index) => (
                      <th
                        key={colonne || index}
                        className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                      >
                        {colonne || <span className="sr-only">Actions</span>}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {total === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray">
                      Aucun entretien sur ce périmètre
                    </td>
                  </tr>
                )}
                {entretiensAffiches.map((entretien) => (
                  <LigneSuivi
                    key={entretien.id}
                    entretien={entretien}
                    onCommencer={() => setFicheOuverte(entretien.id)}
                    onCompteRendu={() => setCompteRenduOuvert(entretien.id)}
                    onOuvrirPdf={() => setPdfOuvert(entretien.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={entretiensAffiches.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="entretien"
          />
        )}
        {issue.error instanceof ErreurApi && (
          <div className="border-t border-gray-100 p-4 sm:p-6">
            <MessageErreur message={issue.error.message} />
          </div>
        )}
      </Carte>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Carte
          titre="Alertes de suivi de dossier"
          actions={
            <BoutonSecondaire onClick={() => navigate('/alertes')}>
              Configurer les seuils
            </BoutonSecondaire>
          }
          sansBordure
        >
          <div className="divide-y divide-gray-100">
            {(data?.alertes ?? []).length === 0 ? (
              <p className="px-4 sm:px-6 py-4 text-sm text-codialis-gray">
                Aucune alerte de suivi en attente sur ce périmètre.
              </p>
            ) : (
              (data?.alertes ?? []).map((alerte, index) => (
                <LigneAlerte
                  key={`${alerte.regle}-${index}`}
                  titre={alerte.libelle}
                  detail={`${alerte.message} · ${horodatage(alerte.declencheeLe)}`}
                  ton={alerte.regle === 'DELAI_TRAITEMENT_DOSSIER' ? 'danger' : 'warning'}
                />
              ))
            )}
          </div>
        </Carte>

        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
            <h3 className="font-display font-bold text-codialis-navy">Relances automatiques</h3>
            <div className="flex items-center gap-2" />
          </div>
          <div className="space-y-3 p-4 sm:p-6">
            {(relances?.scenarios ?? []).length === 0 ? (
              <p className="text-sm text-codialis-gray">Aucun scénario de relance configuré.</p>
            ) : (
              (relances?.scenarios ?? []).map((scenario) => (
                <div
                  key={scenario.code}
                  className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-codialis-navy">{scenario.libelle}</div>
                    <div className="text-[11px] text-codialis-gray">{scenario.declencheur}</div>
                  </div>
                  <span
                    className={`whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold ${
                      scenario.actif ? 'badge-success' : 'badge-neutral'
                    }`}
                  >
                    {scenario.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              ))
            )}
            <button
              type="button"
              onClick={() => navigate('/com-relances')}
              className="gradient-mint text-codialis-navy rounded-xl px-4 py-2.5 text-sm font-semibold transition"
            >
              Gérer les relances
            </button>
          </div>
        </div>
      </div>

      {entretienFiche && referentielFiche && (
        <ModaleFicheEntretien
          entretien={entretienFiche}
          referentiel={referentielFiche}
          formations={referentiels?.formations ?? []}
          peutModifierDossier={peutAcceder('adm-dossier')}
          enCours={issue.isPending || dossierModifie.isPending}
          erreur={erreurIssue}
          onEnregistrer={(valeurs, terminer) =>
            enregistrerFiche(entretienFiche.id, entretienFiche.apprenantId, valeurs, terminer)
          }
          onFermer={() => setFicheOuverte(null)}
        />
      )}

      {entretienCompteRendu && referentielFiche && (
        <ModaleCompteRendu
          entretien={entretienCompteRendu}
          referentiel={referentielFiche}
          enCours={issue.isPending}
          erreur={erreurIssue}
          onDecision={(decision: Decision, orientation?: OrientationEntretien) =>
            trancher(entretienCompteRendu.id, decision, orientation)
          }
          onVerser={() => verser(entretienCompteRendu.id)}
          versementEnCours={versement.isPending}
          versementFait={versement.isSuccess && versement.variables === entretienCompteRendu.id}
          onModifierFiche={() => {
            setCompteRenduOuvert(null);
            setFicheOuverte(entretienCompteRendu.id);
          }}
          onOuvrirPdf={() => setPdfOuvert(entretienCompteRendu.id)}
          onFermer={() => setCompteRenduOuvert(null)}
        />
      )}

      {entretienPdf && (
        <VisionneuseCompteRendu
          ouverte
          entretien={entretienPdf}
          onFermer={() => setPdfOuvert(null)}
        />
      )}
    </div>
  );
}

/**
 * Ligne du tableau de suivi. Elle ne porte aucune saisie : l'entretien se mène
 * dans la fiche de suivi, et se conclut depuis le compte-rendu. La ligne ne
 * fait qu'ouvrir l'une ou l'autre.
 */
function LigneSuivi({
  entretien,
  onCommencer,
  onCompteRendu,
  onOuvrirPdf,
}: {
  entretien: LigneEntretien;
  onCommencer: () => void;
  onCompteRendu: () => void;
  onOuvrirPdf: () => void;
}): JSX.Element {
  const estCandidat = entretien.type === 'CANDIDAT';
  // Un entretien sans rien de saisi n'a pas de compte-rendu à montrer : l'API
  // refuserait d'en composer un, autant ne pas proposer le bouton.
  const compteRenduDisponible = Boolean(
    entretien.fiche ?? entretien.compteRendu ?? entretien.besoinsRecueillis,
  );

  return (
    <tr className="transition hover:bg-gray-50">
      <td className="px-4 py-3">
        {entretien.apprenantId ? (
          <Link
            to={`/adm-dossier/${entretien.apprenantId}`}
            className="font-semibold text-codialis-navy hover:text-codialis-sky"
          >
            {entretien.interlocuteur}
          </Link>
        ) : (
          <span className="font-semibold text-codialis-navy">{entretien.interlocuteur}</span>
        )}
        {entretien.formation && (
          <div className="text-[11px] text-codialis-gray">{entretien.formation}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <Pastille
          texte={estCandidat ? 'Candidat' : 'Entreprise'}
          ton={estCandidat ? 'info' : 'neutral'}
        />
      </td>
      <td className="px-4 py-3 text-xs text-codialis-gray">{entretien.objet ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-xs text-codialis-gray">
        {horodatage(entretien.dateEntretien)}
      </td>
      <td className="px-4 py-3 text-xs text-codialis-gray">{entretien.charge ?? '—'}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Pastille texte={entretien.etatLibelle} ton={entretien.etatTon} />
          {entretien.sites.map((code) => (
            <PastilleSite key={code} site={code} />
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCommencer}
            className={
              entretien.clos
                ? 'inline-flex min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50'
                : 'inline-flex min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg bg-codialis-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-codialis-deep'
            }
          >
            {entretien.clos ? (
              <PencilLine className="h-4 w-4" aria-hidden="true" />
            ) : (
              <PlayCircle className="h-4 w-4" aria-hidden="true" />
            )}
            {/* Une décision prise (retenu, non retenu, réalisé) ferme
                l'entretien : « Reprendre » suggérait à tort qu'il restait à
                mener, alors qu'il ne reste plus qu'à corriger la fiche. */}
            {entretien.clos ? 'Modifier' : entretien.fiche ? 'Reprendre' : 'Commencer l’entretien'}
          </button>
          <button
            type="button"
            onClick={onCompteRendu}
            disabled={!compteRenduDisponible}
            title={
              compteRenduDisponible
                ? undefined
                : 'Le compte-rendu s’ouvre une fois la fiche de suivi renseignée.'
            }
            className="inline-flex min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Compte-rendu
          </button>
          <button
            type="button"
            onClick={onOuvrirPdf}
            disabled={!compteRenduDisponible}
            title={
              compteRenduDisponible
                ? 'Consulter le compte-rendu PDF'
                : 'Le compte-rendu PDF est disponible une fois la fiche de suivi renseignée.'
            }
            className="inline-flex min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileText className="h-4 w-4 text-codialis-sky" aria-hidden="true" />
            PDF
          </button>
        </div>
      </td>
    </tr>
  );
}

function FormulairePlanification({
  site,
  charges,
  enCours,
  erreur,
  onPlanifier,
}: {
  site: string;
  charges: { id: string; nom: string }[];
  enCours: boolean;
  erreur: string | null;
  onPlanifier: (entree: {
    apprenantId?: string;
    entrepriseId?: string;
    dateEntretien: string;
    objet: string;
    chargeId?: string;
  }) => void;
}): JSX.Element {
  const [cible, setCible] = useState<'CANDIDAT' | 'ENTREPRISE'>('CANDIDAT');
  const [identifiant, setIdentifiant] = useState('');
  const [date, setDate] = useState('');
  const [objet, setObjet] = useState('Entretien de recrutement');
  const [charge, setCharge] = useState('');

  const candidats = useApprenants({ site });
  const entreprises = useEntreprises({ site });

  const complet = Boolean(identifiant && date && objet.trim().length >= 3);

  return (
    <Carte titre="Planifier un entretien" sansBordure>
      <form
        className="grid grid-cols-1 gap-4 p-4 sm:p-6 md:grid-cols-2"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (!complet || enCours) return;
          onPlanifier({
            ...(cible === 'CANDIDAT'
              ? { apprenantId: identifiant }
              : { entrepriseId: identifiant }),
            dateEntretien: date,
            objet: objet.trim(),
            ...(charge ? { chargeId: charge } : {}),
          });
        }}
      >
        <fieldset>
          <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Interlocuteur
          </legend>
          <div className="flex gap-2">
            {(['CANDIDAT', 'ENTREPRISE'] as const).map((valeur) => (
              <button
                key={valeur}
                type="button"
                aria-pressed={cible === valeur}
                onClick={() => {
                  setCible(valeur);
                  setIdentifiant('');
                  setObjet(
                    valeur === 'CANDIDAT' ? 'Entretien de recrutement' : 'Recueil des besoins',
                  );
                }}
                className={`min-w-0 flex-1 rounded-xl border py-2.5 text-sm font-semibold transition ${
                  cible === valeur
                    ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                    : 'border-gray-200 bg-gray-50 text-codialis-gray'
                }`}
              >
                {valeur === 'CANDIDAT' ? 'Candidat' : 'Entreprise'}
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label
            htmlFor="comentretiens-interlocuteur"
            className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
          >
            {cible === 'CANDIDAT' ? 'Candidat' : 'Entreprise'}
          </label>
          <select
            id="comentretiens-interlocuteur"
            value={identifiant}
            onChange={(evenement) => setIdentifiant(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          >
            <option value="">Choisir…</option>
            {cible === 'CANDIDAT'
              ? lignesCompletes(candidats.data).map((candidat) => (
                  <option key={candidat.id} value={candidat.id}>
                    {candidat.prenom} {candidat.nom}
                  </option>
                ))
              : (entreprises.data?.entreprises ?? []).map((entreprise) => (
                  <option key={entreprise.id} value={entreprise.id}>
                    {entreprise.denomination}
                  </option>
                ))}
          </select>
        </div>

        <div>
          <label htmlFor="comentretiens-date-et-heure" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Date et heure
          </label>
          <input id="comentretiens-date-et-heure"
            type="datetime-local"
            value={date}
            onChange={(evenement) => setDate(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          />
        </div>

        <div>
          <label htmlFor="comentretiens-objet" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Objet
          </label>
          <input id="comentretiens-objet"
            value={objet}
            onChange={(evenement) => setObjet(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          />
        </div>

        <div>
          <label htmlFor="comentretiens-charge-e-du-suivi" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Chargé(e) du suivi
          </label>
          <select id="comentretiens-charge-e-du-suivi"
            value={charge}
            onChange={(evenement) => setCharge(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          >
            <option value="">Moi-même</option>
            {charges.map((entree) => (
              <option key={entree.id} value={entree.id}>
                {entree.nom}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-3 md:col-span-2 min-w-0">
          {erreur && <MessageErreur message={erreur} />}
          <BoutonPrincipal type="submit" disabled={!complet || enCours}>
            {enCours ? 'Planification…' : 'Planifier l’entretien'}
          </BoutonPrincipal>
        </div>
      </form>
    </Carte>
  );
}
